<template>
  <div class="editor">
    <editor-menu
      v-if="menu"
      class="editor__menu"
      :editor="editor"
      :bold="bold"
      :italic="italic"
      :underline="underline"
      :strike="strike"
      :bullet-list="bulletList"
      :ordered-list="orderedList"
      :blockquote="blockquote"
      :link="link"
      :line="line"
      :table="table"
      :heading="heading"
      :undo="history"
      :redo="history"
      small
    />
    <editor-content
      class="editor__content dynamic-content"
      :class="{ 'with-menu': menu, 'with-frame': frame, 'has-error': error }"
      :editor="editor"
    />
    <p class="error-message" v-if="error">{{ error }}</p>
  </div>
</template>

<script>
import { Editor, EditorContent } from "tiptap";
import {
  Bold,
  Italic,
  Underline,
  Strike,
  OrderedList,
  BulletList,
  ListItem,
  History,
  Link,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  Heading,
  HardBreak,
  HorizontalRule,
  Blockquote,
  TrailingNode,
  Placeholder
} from "tiptap-extensions";

import EditorMenu from "%admin/components/global/EditorMenu";

export default {
  name: "editor",

  components: {
    EditorContent,
    EditorMenu
  },

  props: {
    menu: Boolean,
    frame: Boolean,

    bold: Boolean,
    italic: Boolean,
    underline: Boolean,
    strike: Boolean,
    bulletList: Boolean,
    orderedList: Boolean,
    blockquote: Boolean,
    link: Boolean,
    line: Boolean,
    table: Boolean,
    heading: Array,
    history: Boolean,

    placeholder: String,

    readonly: Boolean,

    hardBreak: Boolean,
    trailingParagraph: Boolean,

    value: {
      type: String,
      default: ""
    },

    error: String
  },

  data() {
    return {
      editor: null,
      emitAfterOnUpdate: false
    };
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      editable: !this.readonly,
      onUpdate: ({ getHTML }) => {
        this.emitAfterOnUpdate = true;
        this.$emit("input", getHTML().replace(/<p><\/p>/, ""));
      },
      onBlur: event => this.$emit("blur", event),
      extensions: this.prepareExtensions()
    });
  },

  methods: {
    focus() {
      this.editor.view.dom.focus();
    },

    prepareExtensions() {
      return [
        this.bold && new Bold(),
        this.italic && new Italic(),
        this.underline && new Underline(),
        this.strike && new Strike(),
        this.orderedList && new OrderedList(),
        this.bulletList && new BulletList(),
        this.orderedList && new ListItem(),
        this.history && new History(),
        this.link && new Link(),
        this.table &&
          new Table({
            resizable: true
          }),
        this.placeholder &&
          new Placeholder({
            showOnlyCurrent: false,
            emptyNodeClass: "is-empty",
            emptyNodeText: this.placeholder
          }),
        this.table && new TableHeader(),
        this.table && new TableCell(),
        this.table && new TableRow(),
        this.heading && new Heading({ levels: this.heading }),
        this.hardBreak && new HardBreak(),
        this.line && new HorizontalRule(),
        this.blockquote && new Blockquote(),
        this.trailingParagraph &&
          new TrailingNode({
            node: "paragraph",
            notAfter: ["paragraph"]
          })
      ].filter(x => x);
    }
  },

  watch: {
    value(val) {
      if (this.emitAfterOnUpdate) {
        this.emitAfterOnUpdate = false;
        return;
      }

      this.editor && this.editor.setContent(val);
    },

    readonly(val) {
      this.editor &&
        this.editor.setOptions({
          editable: !val
        });
    }
  },

  beforeDestroy() {
    this.editor && this.editor.destroy();
  }
};
</script>

<style lang="scss" scoped>
.editor {
  display: flex;
  flex-flow: column nowrap;

  &__menu {
    flex: 0 1 auto;
  }

  &__content {
    height: 100%;
    flex: 1 1 auto;

    &.with-frame {
      border: solid 1px $grey-lighter;
      border-radius: $radius-small $radius-small $radius-small $radius-small;
      padding: 1rem;

      &.has-error {
        border-color: $red;
      }
    }

    &.with-menu {
      position: relative;
      top: -1px;
      border-top-left-radius: 0;
    }

    &::v-deep .is-empty:only-child::before {
      content: attr(data-empty-text);
      float: left;
      color: $grey-lighter;
      pointer-events: none;
      height: 0;
    }
  }

  .error-message {
    color: $red;
    font-size: 0.75rem;
    margin-top: 0.25rem;
    font-family: $family-sans-serif;
  }
}
</style>
